/**
 * UC-22: EvaluateMilestone
 * TOGAF Training context ABB — sandbox milestone checks without UI deps.
 */
import { BuildingType } from '../../../types';
import type { CityStats, Milestone, ResearchState } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';

export interface EvaluateMilestoneInput {
  milestones: Milestone[];
  stats: CityStats;
  research: ResearchState;
  buildingCounts: Record<string, number>;
}

export interface MilestoneReward {
  milestoneId: string;
  name: string;
  reward: number;
}

export interface EvaluateMilestoneOutput {
  milestones: Milestone[];
  newlyCompleted: MilestoneReward[];
  totalReward: number;
}

export interface EvaluateMilestoneDependencies {
  logger?: IAppLogger;
}

export class EvaluateMilestoneUseCase {
  constructor(private readonly deps: EvaluateMilestoneDependencies = {}) {}

  execute(input: EvaluateMilestoneInput): EvaluateMilestoneOutput {
    const newlyCompleted: MilestoneReward[] = [];

    const milestones = input.milestones.map((ms) => {
      if (ms.completed) return ms;

      if (this.isMilestoneMet(ms, input)) {
        newlyCompleted.push({
          milestoneId: ms.id,
          name: ms.name,
          reward: ms.reward,
        });
        return { ...ms, completed: true };
      }

      return ms;
    });

    const totalReward = newlyCompleted.reduce((sum, r) => sum + r.reward, 0);

    this.deps.logger?.log('debug', 'training.milestones', {
      total: input.milestones.length,
      newlyCompleted: newlyCompleted.length,
      totalReward,
    });

    return { milestones, newlyCompleted, totalReward };
  }

  private isMilestoneMet(
    ms: Milestone,
    input: EvaluateMilestoneInput,
  ): boolean {
    const { stats, research, buildingCounts } = input;
    let isMet = false;

    // Budget milestones
    if (ms.targetType === 'budget' && stats.budget >= ms.targetValue) isMet = true;

    // Compliance milestones
    if (ms.targetType === 'compliance' && stats.compliance >= ms.targetValue) isMet = true;

    // Tech completion milestones — check specific tech IDs
    if (ms.id === 'ms_research' && research.completedTechs.includes('tech_basic_sec')) {
      isMet = true;
    }
    if (ms.id === 'ms_soc2' && research.completedTechs.includes('tech_soc2')) isMet = true;

    // Building-specific milestones
    if (ms.id === 'ms_zerotrust' && (buildingCounts[BuildingType.ZeroTrust] || 0) >= 1) {
      isMet = true;
    }
    if (ms.id === 'ms_connect' && (buildingCounts[BuildingType.Pipeline] || 0) >= 3) {
      isMet = true;
    }
    if (ms.id === 'ms_first_hire' && (buildingCounts[BuildingType.SecurityAML] || 0) >= 1) {
      isMet = true;
    }
    if (
      ms.id === 'ms_gdpr' &&
      (buildingCounts[BuildingType.GDPROffice] || 0) >= 1 &&
      stats.compliance >= 95
    ) {
      isMet = true;
    }

    // Zero breaches milestone — requires tracking consecutive ticks (skip for now)
    // ms_zero_breaches is handled separately

    // Product count milestones — only check if targetValue > 0
    if (ms.targetType === 'product_count' && ms.targetValue > 0) {
      if (stats.activeProducts.length >= ms.targetValue) isMet = true;
    }

    return isMet;
  }
}